import {
  createAction,
  createActionGroup,
  emptyProps,
  props,
} from '@ngrx/store';
import {
  Challenge,
  DiagnosisChallenge,
} from '../../../common/models/master-data/challenge';
import { DataEntryCategory } from '../../../common/models/master-data/data-entry';
import { Reference } from '../../../common/models/master-data/metadata';
import {
  DiagnosisDataEntry,
  DiagnosisDataEntryCreateDto,
  DiagnosisDataEntryGetDto,
} from '../../../common/models/workflow/diagnosis-entry';
import { DiagnosisRequiredAction } from '../../../common/models/workflow/diagnosis-metadata';
import {
  DiagnosisSession,
  InitialSession,
} from '../../../common/models/workflow/diagnosis-session';
import { DiagnosisSolutionCompatibility } from '../../../common/models/workflow/diagnosis-solution';
import { VDRJob } from '../../../common/models/workflow/vdr-job';

export const loadDataByDiagnosisId = createAction(
  '[Workflow] Load Data By Diagnosis Id',
  props<{
    diagnosisId: string;
  }>(),
);

export const loadChallenges = createAction(
  '[Workflow] Load Challenges from API',
);

export const loadChallengesByPSL = createAction(
  '[Workflow] Load Challenges by PSL',
  props<{
    pslId: string;
  }>(),
);

export const LoadChallengesSuccess = createAction(
  '[Workflow] Load Challenges Successfully',
  props<{
    challenges: Challenge[];
  }>(),
);

export const LoadChallengesFailure = createAction(
  '[Workflow] Load Challenges Failure',
  props<{
    error: unknown;
  }>(),
);

export const setPairChallenges = createAction(
  '[Workflow] Set Pair Challenges',
  props<{
    diagnosisId: string;
    subChallengeId: string;
  }>(),
);

export const deletePairChallenges = createAction(
  '[Workflow] Delete Pair Challenge',
  props<{
    diagnosisId: string;
    subChallengeId: string;
  }>(),
);

export const addPairChallenge = createAction(
  '[Workflow] Add Pair Challenges',
  props<{
    diagnosisId: string;
    subChallengeId: string;
  }>(),
);

export const updatePairChallenge = createAction(
  '[Workflow] Update Pair Challenge',
  props<{
    diagnosisId: string;
    subChallengeId: string;
  }>(),
);

export const loadPairChallengesFailure = createAction(
  '[Workflow] Load Pair Challenges Failure',
  props<{
    modifiedInfo: string;
  }>(),
);

export const loadSeletedChallenges = createAction(
  '[Workflow] Load Selected Challenges from API',
  props<{
    diagnosisId: string;
  }>(),
);

export const loadSeletedChallengesSuccess = createAction(
  '[Workflow] Load Selected Challenges Successfully',
  props<{
    diagnosisChallenges: DiagnosisChallenge[];
  }>(),
);

export const loadSeletedChallengesFailure = createAction(
  '[Workflow] Load Selected Challenges Failure',
  props<{
    error: unknown;
  }>(),
);

export const filterSubChallenge = createAction(
  '[Workflow] filter Sub Challenges by Challenge Id',
  props<{
    challengeId: string;
  }>(),
);

export const initializeSession = createAction(
  '[Workflow] Initialize Session',
  props<{
    initialSession: InitialSession;
  }>(),
);

export const initializeSessionSuccess = createAction(
  '[Workflow] Initialize Session Successfully',
  props<{
    diagnosisSession: DiagnosisSession;
  }>(),
);

export const initializeSessionFailure = createAction(
  '[Workflow] Initialize Session Failure',
  props<{
    error: unknown;
  }>(),
);

export const setSelectedChallengeLoadingState = createAction(
  '[Workflow] Set Selected Challenge Loading State',
  emptyProps,
);

export const SessionActions = createActionGroup({
  source: 'Session',
  events: {
    'Start New Workflow': emptyProps(),
    'Complete VDR Session': props<{ diagnosisId: string }>(),
    'Complete One Off Session': props<{ diagnosisId: string }>(),
    'Reset Session': props<{ diagnosisId: string; pslCode: string }>(),
    'Reopen Session': props<{ diagnosisId: string }>(),
    'Cancel Session': props<{ diagnosisId: string }>(),
    'Load Session Information': props<{ diagnosisId: string }>(),
  },
});

export const SessionAPIActions = createActionGroup({
  source: 'Session',
  events: {
    'Complete VDR Session Success': props<{ session: DiagnosisSession }>(),
    'Complete VDR Session Failure': props<{
      modifiedInfo: string;
    }>(),
    'Complete One Off Session Success': props<{ session: DiagnosisSession }>(),
    'Complete One Off Session Failure': props<{
      modifiedInfo: string;
    }>(),
    'Reset Session Success': props<{ session: DiagnosisSession }>(),
    'Load Session Information Success': props<{ session: DiagnosisSession }>(),
    'Reopen Session Success': props<{ session: DiagnosisSession }>(),
    'Reopen Session Failure': props<{
      modifiedInfo: string;
    }>(),
    'Cancel Session Success': props<{ session: DiagnosisSession }>(),
    'Request Failure': props<{ error: string }>(),
  },
});

export const VDRJobActions = createActionGroup({
  source: 'VDRJob',
  events: {
    'Load VDR Job Information': props<{ diagnosisId: string }>(),
  },
});

export const VDRJobAPIActions = createActionGroup({
  source: 'VDRJob API',
  events: {
    'Load VDR Job Information Success': props<{
      vdrJob: VDRJob;
    }>(),

    'Request Failure': props<{ error: string }>(),
  },
});

export const SolutionActions = createActionGroup({
  source: 'Solution',
  events: {
    'Choose Solution': props<{ diagnosisId: string; solutionId: string }>(),
    'Remove Solution': props<{ diagnosisId: string; solutionId: string }>(),
    'Remove All Solutions': props<{ diagnosisId: string }>(),
    'Get Selected Solutions': props<{ diagnosisId: string }>(),
    'Get Available Solutions': props<{ diagnosisId: string }>(),
    'Get Solution Compatibilities': props<{ diagnosisId: string }>(),
  },
});

export const SolutionAPIActions = createActionGroup({
  source: 'Solution API',
  events: {
    'Choose Solution Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
      selectedSolutionId: string;
    }>(),
    'Choose Solution Failure': props<{
      modifiedInfo: string;
    }>(),
    'Remove Solution Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
      selectedSolutionId: string;
    }>(),
    'Remove Solution Failure': props<{
      modifiedInfo: string;
    }>(),
    'Remove All Solutions Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Remove All Solutions Failure': props<{
      modifiedInfo: string;
    }>(),
    'Get Selected Solutions Success': props<{
      solutionIds: string[];
    }>(),
    'Get Available Solutions Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Get Solution Compatibilities Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Set Solution Loading State': emptyProps(),
    'Request Failure': props<{ error: string }>(),
  },
});

export const CustomerSpecificationActions = createActionGroup({
  source: 'Customer Specification',
  events: {
    'Upsert Entry Value': props<{ entryValue: DiagnosisDataEntryCreateDto }>(),
    'Remove Entry Value': props<{ entryValue: DiagnosisDataEntryGetDto }>(),
    'Remove All Entry Values': props<{ diagnosisId: string }>(),
    'Get Entered Data Entries': props<{ diagnosisId: string }>(),
    'Get Suggested Data Entries': props<{ diagnosisId: string }>(),
    'Load Data Entries By Diagnosis Id': props<{ diagnosisId: string }>(),
  },
});

export const CustomerSpecificationAPIActions = createActionGroup({
  source: 'Customer Specification API',
  events: {
    'Upsert Entry Value Success': props<{
      diagnosisId: string;
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Upsert Entry Value Failure': props<{
      modifiedInfo: string;
    }>(),
    'Remove Entry Value Success': props<{
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Remove Entry Value Failure': props<{
      modifiedInfo: string;
    }>(),
    'Remove All Entry Values Success': props<{
      diagnosisId: string;
      solutions: DiagnosisSolutionCompatibility[];
    }>(),
    'Remove All Entry Values Failure': props<{
      modifiedInfo: string;
    }>(),
    'Load Entered Data Entries When Session Completed': props<{
      enteredValues: DiagnosisDataEntry[];
    }>(),
    'Get Entered Data Entries Success': props<{
      diagnosisId: string;
      enteredValues: DiagnosisDataEntry[];
    }>(),
    'Load Suggested Data Entries Success': props<{
      diagnosisId: string;
      dataEntries: DataEntryCategory[];
    }>(),
    'Set Data Entry Loading State': emptyProps(),
    'Request Failure': props<{ error: string }>(),
  },
});

export const AdditionalActions = createActionGroup({
  source: 'Additional Actions',
  events: {
    'Load Solution References': props<{ diagnosisId: string }>(),
    'Load Required Actions': props<{ diagnosisId: string }>(),
  },
});

export const AdditionalAPIActions = createActionGroup({
  source: 'Additional API Actions',
  events: {
    'Load Solution References Success': props<{
      references: Reference[];
    }>(),
    'Load Required Actions Success': props<{
      requiredActions: DiagnosisRequiredAction[];
    }>(),
    'Set Required Action Loading State': emptyProps(),
    'Set Solution Reference Loading State': emptyProps(),

    'Request Failure': props<{ error: string }>(),
  },
});
